import React, { useMemo, useEffect, useCallback, useState, useRef, memo } from 'react';
import { throttle } from 'lodash';
import { Map, Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import { Box, IconButton, Typography, Badge } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import 'maplibre-gl/dist/maplibre-gl.css';
import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';
import { useMapState } from '../../../hooks/useMapState.js';
import { useMapSync } from './map_syncro.js';
import { getISOWeekAndYear } from '../../../utils/dateUtils.js';
import MapSynchronizer from './sync_maps.js';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import {
  generateGeoJSONFileName,
  TILE_LAYER_URL,
  BOUNDS_RECTANGLE_STYLE,
  MASK_POLYGON_STYLE,
  MAP_CONFIG
} from '../../../utils/mapUtils.js';
import LegendMap, { LEGEND_CLEARANCE } from './map_legend.js';
import { STOPS as TURBULENCE_STOPS } from '../../layers_selector/turbulence/risk-selector_turbulence.js';
import { getDustFrameTime } from '../../../utils/forecastMetricsDateUtils.js';
import FirLayers, { FirToggle } from '../../common/FirLayers.js';
import MapLoadingOverlay, { MapNotice } from '../../common/MapLoadingOverlay.js';
import MetricsInsideMap from '../metrics_inside_map.js';
import { prefetchPmtiles } from '../../../utils/pmtilesProtocol.js';
import useNearestData, { closestHour, SEARCHING_NEAREST } from '../../../hooks/useNearestData.js';

function buildPredFilename(date, prefix = 'pred') {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${prefix}_${d.getUTCFullYear()}` +
    `${pad(d.getUTCMonth() + 1)}` +
    `${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}` +
    `${pad(d.getUTCMinutes())}` +
    `${pad(d.getUTCSeconds())}Z.pmtiles`
  );
}

// Clases de VISIBILITY_MATRIX_RISK: mismos colores y rangos que visibility live (/weather-map)
const VISIBILITY_CLASSES = {
  1: { color: 'green', label: '> 1000 m' },
  2: { color: 'blue', label: '600 - 1000 m' },
  3: { color: 'yellow', label: '350 - 600 m' },
  4: { color: 'red', label: '< 350 m' },
};

const visibilityLayerStyle = {
  id: 'visibility-circle',
  type: 'circle',
  layout: { 'circle-sort-key': ['to-number', ['get', 'VISIBILITY_MATRIX_RISK']] }, // peor visibilidad encima
  paint: {
    'circle-radius': 4.35,
    'circle-stroke-color': '#000000',
    'circle-stroke-width': 1.25,
    'circle-color': [
      'match', ['to-number', ['get', 'VISIBILITY_MATRIX_RISK']],
      ...Object.entries(VISIBILITY_CLASSES).flatMap(([risk, { color }]) => [Number(risk), color]),
      'gray'
    ],
  },
};

// Clases de dust: los 3 colores del raster (= relleno de dust live en /weather-map) con las etiquetas de su leyenda
const DUST_CLASSES = [
  { color: '#d2bfa8', label: 'Low' },
  { color: '#967157', label: 'Medium' },
  { color: '#3f2b21', label: 'High' },
];

// Clases de ICE (HAIC): los 3 colores exactos del raster con las etiquetas de su leyenda en /weather-map
const ICE_CLASSES = [
  { color: '#87ceeb', label: 'Light' },
  { color: '#4191dc', label: 'Moderate' },
  { color: '#4b41b4', label: 'Severe' },
];

// Capas con popup al pasar el ratón
const HOVER_LAYERS = ['cnn-layer', 'visibility-circle'];

function extractIssueHour(issueTime) {
  if (!issueTime) return '01';
  const m = issueTime.match(/[T\s](\d{2}):/);
  return m ? m[1] : '01';
}

// ─── Inner memo component ────────────────────────────
const MapLeftContent = memo(({
  mapLeftLayer,
  geoJsonData,
  loading,
  hasData,
  rectBounds,
  maskPolygon,
  cqlFecha,
  selectedTime,
  isInteractive,
  showMosaic,
  showGeoJson,
  pmtilesHistoricalUrl,
  validTime,
  retry,
}) => {
  const { current: map } = useMap();
  const [hoverInfo, setHoverInfo] = useState(null);
  const isMoving = useRef(false);

  useEffect(() => {
    if (!map) return;
    const nativeMap = map.getMap();

    const onMouseMoveNative = throttle((e) => {
      if (isMoving.current) return;
      // Solo capas montadas: si alguna no existe queryRenderedFeatures lanza error y devuelve []
      const layers = HOVER_LAYERS.filter((id) => nativeMap.getLayer(id));
      const features = layers.length ? nativeMap.queryRenderedFeatures(e.point, { layers }) : [];
      if (features && features[0]) {
        setHoverInfo({ feature: features[0], lngLat: e.lngLat });
      } else {
        setHoverInfo(null);
      }
    }, 150);

    const onMouseLeaveNative = () => {
      setHoverInfo(null);
      onMouseMoveNative.cancel();
    };

    const handleMovementStart = () => { isMoving.current = true; setHoverInfo(null); };
    const handleMovementEnd = () => { isMoving.current = false; };

    nativeMap.on('mousemove', HOVER_LAYERS, onMouseMoveNative);
    nativeMap.on('mouseleave', HOVER_LAYERS, onMouseLeaveNative);
    nativeMap.on('movestart', handleMovementStart);
    nativeMap.on('moveend', handleMovementEnd);
    nativeMap.on('zoomstart', handleMovementStart);
    nativeMap.on('zoomend', handleMovementEnd);

    return () => {
      nativeMap.off('mousemove', HOVER_LAYERS, onMouseMoveNative);
      nativeMap.off('mouseleave', HOVER_LAYERS, onMouseLeaveNative);
      nativeMap.off('movestart', handleMovementStart);
      nativeMap.off('moveend', handleMovementEnd);
      nativeMap.off('zoomstart', handleMovementStart);
      nativeMap.off('zoomend', handleMovementEnd);
      onMouseMoveNative.cancel();
    };
  }, [map]);

  const cnnLayerStyle = useMemo(() => ({
    id: 'cnn-layer',
    type: 'fill',
    paint: {
      'fill-color': [
        'match',
        ['get', 'CONVECTIVE_MATRIX_RISK'],
        3, '#DC143C',
        2, '#FF8C00',
        1, '#ffff00',
        'transparent'
      ],
      'fill-opacity': 0.75,
      'fill-outline-color': '#a3a1a1c0'
    }
  }), []);

  const mapBounds = useMemo(() => {
    if (!rectBounds) return null;
    return [rectBounds[0][1], rectBounds[0][0], rectBounds[1][1], rectBounds[1][0]];
  }, [rectBounds]);

  useEffect(() => {
    if (map && mapBounds) {
      // El contenedor puede acabar de cambiar de ancho (turbulence ↔ CNN) y MapLibre solo se entera más tarde por
      // ResizeObserver: sin esto el fit se calcula con el tamaño viejo y la capa queda descentrada
      map.resize();
      map.fitBounds(mapBounds, { padding: 60, duration: 800 });
    }
  }, [map, mapBounds]);

  const maskGeoJson = useMemo(() => {
    if (!maskPolygon) return null;
    const [outer, inner] = maskPolygon;
    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [
          outer.map(([lat, lon]) => [lon, lat]),
          inner.map(([lat, lon]) => [lon, lat])
        ]
      }
    };
  }, [maskPolygon]);

  const isVisibilityLayer = mapLeftLayer && mapLeftLayer.includes('VISIBILITY');

  // Popup según la capa: aeropuerto (visibility) o celda convectiva (CNN)
  let hoverContent = null;
  if (hoverInfo) {
    const p = hoverInfo.feature.properties;
    const vis = VISIBILITY_CLASSES[Number(p.VISIBILITY_MATRIX_RISK)];
    hoverContent = hoverInfo.feature.layer.id === 'visibility-circle'
      ? { title: `Visibility · ${p.AIRPORT}`, color: vis?.color, text: vis?.label }
      : {
        title: 'Storm Risk',
        color: ['transparent', '#ffff00', '#FFA500', '#DC143C'][p.CONVECTIVE_MATRIX_RISK],
        text: ['', 'Low', 'Medium', 'High'][p.CONVECTIVE_MATRIX_RISK],
      };
  }

  return (
    <>
      <MapSynchronizer />
      {/* key por capa: al cambiar de capa (convection ↔ turbulence) se desmonta la fuente y MapLibre tira sus teselas al
          momento; con setUrl las seguía pintando hasta que llegaba cada nueva. Entre horas de la misma capa sigue el
          setUrl, así el slider no parpadea. Retry también la monta de nuevo */}
      {showMosaic && pmtilesHistoricalUrl && (
        <Source
          key={`${mapLeftLayer}-${retry}`}
          id="pmtiles-source"
          type="raster"
          url={`pmtiles://${pmtilesHistoricalUrl}`}
          tileSize={256}
        >
          <Layer
            id="pmtiles-layer"
            type="raster"
            beforeId={maskPolygon ? 'mask-layer' : (geoJsonData ? 'cnn-layer' : undefined)}
            paint={{
              'raster-opacity': 0.8,
              'raster-fade-duration': 0,
              // Dust e ICE son 3 clases (3 colores exactos, teselas solo hasta z2): 'linear' las emborrona, inventa tonos
              // intermedios y deja una costura en lon 0 entre teselas. CNN y turbulence son heatmaps continuos: linear
              'raster-resampling': /DUST|ICE/.test(mapLeftLayer) ? 'nearest' : 'linear'
            }}
          />
        </Source>
      )}

      {/* Polígonos CNN del día entero: el slider solo cambia el filtro de hora */}
      {showGeoJson && geoJsonData && (
        <Source type="geojson" data={geoJsonData}>
          <Layer {...cnnLayerStyle} filter={['==', ['get', 'VALID_TIMESTAMP'], `${validTime.replace(' ', 'T')}Z`]} />
        </Source>
      )}

      {/* Visibility: puntos de aeropuertos (WFS) de todo el día; el slider solo cambia el filtro de hora */}
      {isVisibilityLayer && geoJsonData && (
        <Source type="geojson" data={geoJsonData}>
          <Layer {...visibilityLayerStyle} filter={['==', ['get', 'VALID_TIMESTAMP'], `${validTime.replace(' ', 'T')}Z`]} />
        </Source>
      )}

      {maskGeoJson && (
        <Source type="geojson" data={maskGeoJson}>
          <Layer
            id="mask-layer"
            type="fill"
            paint={{
              'fill-color': MASK_POLYGON_STYLE.fillColor,
              'fill-opacity': MASK_POLYGON_STYLE.fillOpacity
            }}
          />
        </Source>
      )}

      {rectBounds && (
        <Source type="geojson" data={{
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [rectBounds[0][1], rectBounds[0][0]],
              [rectBounds[1][1], rectBounds[0][0]],
              [rectBounds[1][1], rectBounds[1][0]],
              [rectBounds[0][1], rectBounds[1][0]],
              [rectBounds[0][1], rectBounds[0][0]]
            ]
          }
        }}>
          <Layer
            id="bounds-layer"
            type="line"
            paint={{
              'line-color': BOUNDS_RECTANGLE_STYLE.color,
              'line-width': BOUNDS_RECTANGLE_STYLE.weight,
              'line-dasharray': [3, 5]
            }}
          />
        </Source>
      )}

      {hoverContent && (
        <Popup
          longitude={hoverInfo.lngLat.lng}
          latitude={hoverInfo.lngLat.lat}
          closeButton={false}
          anchor="bottom"
        >
          <div style={{
            padding: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderRadius: '16px',
            color: '#1a1a1a',
            border: '1px solid rgba(255, 255, 255, 0.6)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            fontFamily: "'Inter', sans-serif",
            fontSize: '13px',
            minWidth: '150px'
          }}>
            <div style={{ color: 'rgba(0,0,0,0.5)', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
              {hoverContent.title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                backgroundColor: hoverContent.color || '#ccc',
                border: '1px solid rgba(0,0,0,0.1)'
              }} />
              <strong style={{ fontSize: '18px', fontWeight: '900', letterSpacing: '-0.02em' }}>
                {hoverContent.text || 'Unknown'}
              </strong>
            </div>
          </div>
        </Popup>
      )}
    </>
  );
});

const MapLeft = ({
  cqlSevere,
  cqlFecha,
  mapLeftLayer,
  issueTime,
  weekNumber,
  selectedTime,
  isInteractive = true,
  metricsName,
  handleRiskClick,
  label,
  onGoToIssue,
}) => {
  const [geoJsonData, setGeoJsonData] = useState(null);
  // Media hora de visibility ya pintada mientras llega el día entero
  const [slotTime, setSlotTime] = useState(null);
  // Última URL pmtiles con el mapa en reposo (idle): teselas ya pintadas. Un 404 también acaba en idle
  const [idleUrl, setIdleUrl] = useState(null);
  const [showMosaic, setShowMosaic] = useState(true);
  const [showGeoJson, setShowGeoJson] = useState(false);
  const [showFirs, setShowFirs] = useState(true);
  const [historicalConfig, setHistoricalConfig] = useState({ '025': null, '01': null, turbulence: null, dust: null, ice: null });
  // Fallos que antes dejaban el mapa en blanco sin aviso; Retry (retry + 1) repite config, pmtiles y WFS
  const [retry, setRetry] = useState(0);
  const [configFail, setConfigFail] = useState(false);
  const [pmtilesFail, setPmtilesFail] = useState(null); // { url, notFound }
  const [wfsFail, setWfsFail] = useState(null); // 'error' | 'empty'
  const { mapState } = useMapSync();
  const mapRef = useRef(null);

  useEffect(() => {
    setShowGeoJson(false);
    // Fuera los datos de la capa anterior: el círculo de visibility pintaría cada vértice de los polígonos CNN
    setGeoJsonData(null);
    // La fuente pmtiles se monta de nuevo (key por capa): no hay nada pintado de la capa nueva
    setIdleUrl(null);
  }, [mapLeftLayer]);

  const {
    loading,
    showLegend,
    hasData,
    rectBounds,
    maskPolygon,
    setLoading,
    setHasData,
    setShowLegend,
    handleToggleLegend,
    handleResetView,
    selectedModel,
  } = useMapState(mapLeftLayer);

  // Función manual para resetear el zoom
  const handleReset = () => {
    if (mapRef.current && rectBounds) {
      const bounds = [rectBounds[0][1], rectBounds[0][0], rectBounds[1][1], rectBounds[1][0]];
      mapRef.current.fitBounds(bounds, { padding: 60, duration: 1000 });
    }
  };

  useEffect(() => {
    // Una petición por capa: si falla una (p.ej. backend aún sin historical-turbulence) las demás siguen funcionando
    // Antes se pedía una sola vez: si el backend fallaba justo al abrir la página, no salían heatmaps hasta recargar
    const endpoints = { '025': 'historical-cnn-025', '01': 'historical-cnn-01', turbulence: 'historical-turbulence', dust: 'historical-dust', ice: 'historical-ice' };
    setConfigFail(false);
    Object.entries(endpoints).forEach(([key, endpoint]) => {
      axios.get(`${process.env.REACT_APP_API_URL}/api/pmtiles/${endpoint}`, { timeout: 10000 })
        .then((res) => setHistoricalConfig((prev) => ({ ...prev, [key]: res.data.baseUrl })))
        .catch((err) => {
          console.warn(`[MapLeft] Could not fetch ${endpoint} base URL:`, err.message);
          setConfigFail(true);
        });
    });
  }, [retry]);

  // ── Build PMTiles URL whenever controls change ─────────────────────────
  // URL: {baseUrl}/{year}/{month}/{day}/{issue_hour}/pred_YYYYMMDDTHHMMSSZ.pmtiles (CNN y turbulence; ICE: ice_*, horario; dust: dust_*, cada 3 h)
  //
  // • issueTime  = "2025-04-30 01:00:00"  → year/month/day and issue_hour (01)
  // • cqlFecha   = "2025-04-30 07:00:00"  → prediction filename
  const buildPmtilesUrl = useCallback((fecha) => {
    const is025 = mapLeftLayer && mapLeftLayer.includes('025');
    const is01 = mapLeftLayer && mapLeftLayer.includes('01');
    const isTurbulence = mapLeftLayer && mapLeftLayer.includes('TURBULENCE');
    const isDust = mapLeftLayer && mapLeftLayer.includes('DUST');
    const isIce = mapLeftLayer && mapLeftLayer.includes('ICE');
    if (!showMosaic || (!is025 && !is01 && !isTurbulence && !isDust && !isIce) || !fecha || !issueTime) return null;

    const baseUrl = isTurbulence ? historicalConfig.turbulence : isDust ? historicalConfig.dust : isIce ? historicalConfig.ice : is025 ? historicalConfig['025'] : historicalConfig['01'];
    if (!baseUrl) return null;

    try {
      const issueDatePart = issueTime.split(/[T\s]/)[0];
      const [year, month, day] = issueDatePart.split('-');
      const issueHourStr = extractIssueHour(issueTime);
      const issueHour = parseInt(issueHourStr, 10);

      let validTimeStr = (isDust ? getDustFrameTime(fecha, issueTime) : fecha).replace(' ', 'T');
      if (!validTimeStr.endsWith('Z')) validTimeStr += 'Z';

      const predDate = new Date(validTimeStr);
      if (isNaN(predDate.getTime())) return null;

      // Dust ya viene ajustado a su primera imagen del run (issue+2h); con esta regla (solo la hora) pediría 13:00, que no existe
      if (!isDust && predDate.getUTCHours() < issueHour) {
        predDate.setUTCHours(issueHour);
      }

      const filename = buildPredFilename(predDate, isDust ? 'dust' : isIce ? 'ice' : 'pred');
      return `${baseUrl}/${year}/${month}/${day}/${issueHourStr}/${filename}`;
    } catch (e) {
      console.warn('[MapLeft] Error building PMTiles URL:', e);
      return null;
    }
  }, [showMosaic, mapLeftLayer, historicalConfig, issueTime]);
  const pmtilesHistoricalUrl = useMemo(() => buildPmtilesUrl(cqlFecha), [buildPmtilesUrl, cqlFecha]);

  // ── Clamped cqlFecha for Polygons and Metrics ───────────────────────────────
  const clampedCqlFecha = useMemo(() => {
    if (!cqlFecha || !issueTime) return cqlFecha;
    try {
      const issueHourStr = extractIssueHour(issueTime);
      const issueHour = parseInt(issueHourStr, 10);

      let validTimeStr = cqlFecha.replace(' ', 'T');
      if (!validTimeStr.endsWith('Z')) validTimeStr += 'Z';

      const predDate = new Date(validTimeStr);
      if (isNaN(predDate.getTime())) return cqlFecha;

      if (predDate.getUTCHours() < issueHour) {
        predDate.setUTCHours(issueHour);
        const pad = (n) => String(n).padStart(2, '0');
        return `${predDate.getUTCFullYear()}-${pad(predDate.getUTCMonth() + 1)}-${pad(predDate.getUTCDate())} ${pad(predDate.getUTCHours())}:${pad(predDate.getUTCMinutes())}:${pad(predDate.getUTCSeconds())}`;
      }
      return cqlFecha;
    } catch (e) {
      console.warn('[MapLeft] Error clamping cqlFecha:', e);
      return cqlFecha;
    }
  }, [cqlFecha, issueTime]);

  // Visibility: una petición por día + issue y el slider solo filtra la hora (cada 30 min) en el mapa. Memo aparte: el
  // objeto no cambia al mover el slider, así que no se vuelve a pedir. `slot` pide solo la media hora del slider (~200
  // puntos, décimas de segundo) para pintarla mientras llega el día entero (~3 MB, 1-1,5 s si no está en Redis)
  const cqlDay = cqlFecha?.split(' ')[0];
  const visibilityUrls = useMemo(() => {
    if (!cqlDay || !issueTime) return null;
    // Endpoint propio (Redis en el backend); backup = proxy WFS genérico, por si el backend aún no tiene el endpoint
    // Particiones por (YEAR, WEEK_NUMBER) = año y semana ISO de VALID_TIMESTAMP (el 29-12-2025 es YEAR 2026, semana 1):
    // con los dos Postgres abre 1 partición en vez de las 216, y en frío cada partición cuesta lecturas en el NAS.
    // T00:00:00 sin Z = medianoche local, para que el día no se corra en husos al oeste de UTC
    const { year, week } = getISOWeekAndYear(`${cqlDay}T00:00:00`);
    const wfs = (cql) => `service=WFS&version=1.0.0&request=GetFeature&typeName=VISIBILITY:visibility_historical&outputFormat=application/json&CQL_FILTER=${encodeURIComponent(`${cql} AND WEEK_NUMBER=${week} AND YEAR=${year}`)}`;
    const query = wfs(`VALID_TIMESTAMP >= '${cqlDay} 00:00:00' AND VALID_TIMESTAMP <= '${cqlDay} 23:30:00' AND ISSUE_TIMESTAMP='${issueTime}'`);
    return {
      primary: `${process.env.REACT_APP_API_URL}/proxy/wfs/visibility_historical?${query}`,
      backup: `${process.env.REACT_APP_API_URL}/proxy/wfs?${query}`,
      slot: (validTime) => `${process.env.REACT_APP_API_URL}/proxy/wfs/visibility_historical?${wfs(`VALID_TIMESTAMP='${validTime}' AND ISSUE_TIMESTAMP='${issueTime}'`)}`,
    };
  }, [cqlDay, issueTime]);

  // ── GeoJSON URLs for WFS vector layers ───────────────────────────────
  // Polígonos CNN: el día entero del issue en una petición (60–200 polígonos, 5–20 KB con gzip) y el slider solo cambia
  // el filtro de hora, como visibility. Antes cada paso iba a GeoServer: en frío una hora suelta tardaba de 6 a 34 s.
  // Semana y año ISO, como la tabla (el 29-12-2025 es YEAR 2026, semana 1): con YEAR abre una partición y un día en frío
  // baja de ~6 s a 2,5–4 s. El prop weekNumber (date-fns getWeek) empieza en domingo: los domingos daba la semana
  // siguiente y los polígonos salían vacíos. Se queda para las métricas, cuya convención no se ha mirado
  const geoJsonUrls = useMemo(() => {
    if (!cqlDay || !issueTime) return null;
    const { year, week } = getISOWeekAndYear(`${cqlDay}T00:00:00`);
    const cql = encodeURIComponent(`VALID_TIMESTAMP >= '${cqlDay} 00:00:00' AND VALID_TIMESTAMP <= '${cqlDay} 23:59:59' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${week}' AND YEAR=${year}`);
    const cnn = (route, typeName) => `${process.env.REACT_APP_API_URL}/proxy/${route}?service=WFS&version=1.1.0&request=GetFeature&typeName=CNN_HISTORICAL:${typeName}&outputFormat=application/json&CQL_FILTER=${cql}`;

    return {
      "VISIBILITY:visibility_historical": visibilityUrls,
      "CNN_HISTORICAL:EU_025_live_demo": {
        primary: cnn('cnn-forecast-metrics-primary', 'EU_025_live_demo'),
        backup: cnn('cnn-forecast-metrics-primary', 'EU_025_live_demo'),
      },
      "CNN_HISTORICAL:EU_01_live_demo": {
        primary: cnn('cnn-forecast-metrics-primary', 'EU_01_live_demo'),
        backup: cnn('cnn-forecast-metrics-backup', 'EU_01_live_demo'),
      },
      "CNN_HISTORICAL:euro_1k_00625_15m": {
        primary: cnn('cnn-forecast-metrics-primary', 'euro_1k_00625_15m'),
        backup: cnn('cnn-forecast-metrics-backup', 'euro_1k_00625_15m'),
      },
    };
  }, [cqlDay, issueTime, visibilityUrls]);

  // null = fallaron las dos fuentes. Solo vale GeoJSON: GeoServer devuelve sus errores como XML con estado 200.
  // timeout: sin él una petición colgada dejaba el aviso de carga para siempre
  const fetchWithFallback = useCallback(async (urlObj, signal) => {
    if (!urlObj || !urlObj.primary || !urlObj.backup) return null;
    try {
      if (signal?.aborted) return null;
      const resp = await axios.get(urlObj.primary, { signal, timeout: 30000 });
      if (Array.isArray(resp.data?.features)) return resp.data;
      console.warn('Primary source returned no GeoJSON, trying backup...');
    } catch (err) {
      if (axios.isCancel(err)) throw err;
      console.warn('Primary source failed, trying backup...', err.message);
    }

    if (signal?.aborted) return null;
    try {
      const backupResp = await axios.get(urlObj.backup, { signal, timeout: 30000 });
      return Array.isArray(backupResp.data?.features) ? backupResp.data : null;
    } catch (err) {
      if (axios.isCancel(err)) throw err;
      console.error('Backup source also failed:', err.message);
      return null;
    }
  }, []);

  const abortControllerRef = useRef(null);

  const loadGeoJson = useCallback(async (urlObj, validTime) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    setHasData(true);
    setSlotTime(null);
    setWfsFail(null);
    let fullLoaded = false;
    // Visibility: la media hora del slider va en paralelo con el día entero y, si llega antes, se pinta ya.
    // Si falla no pasa nada: el día entero la trae igualmente
    if (urlObj?.slot && validTime) {
      axios.get(urlObj.slot(validTime), { signal: controller.signal, timeout: 30000 })
        .then(({ data }) => {
          if (!fullLoaded && !controller.signal.aborted && data?.features?.length) {
            setGeoJsonData(data);
            setSlotTime(validTime);
          }
        })
        .catch(() => {});
    }
    try {
      const data = await fetchWithFallback(urlObj, controller.signal);
      fullLoaded = true;
      if (controller.signal.aborted) return;
      if (!data || !data.features) {
        setHasData(false);
        setGeoJsonData(null);
        if (urlObj) setWfsFail('error'); // sin urlObj no había nada que pedir (capa pmtiles con POLYGONS apagado)
        return;
      }
      data.features.sort((a, b) => (Number(a.properties.CONVECTIVE_MATRIX_RISK) || 0) - (Number(b.properties.CONVECTIVE_MATRIX_RISK) || 0));
      setGeoJsonData(data);
      setHasData(data.features.length > 0);
      if (!data.features.length) setWfsFail('empty');
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError') return;

      console.error('❌ [MapLeft] Error cargando capa:', error);
      setHasData(false);
      setWfsFail('error');
    } finally {
      if (abortControllerRef.current === controller) setLoading(false);
    }
  }, [fetchWithFallback, setLoading, setHasData]);

  // Los polígonos CNN solo se piden con POLYGONS activo: antes se pedían en cada paso del slider aunque no se vieran
  const geoJsonUrl = (showGeoJson || mapLeftLayer?.includes('VISIBILITY')) ? geoJsonUrls?.[mapLeftLayer] : null;

  // Una petición por cambio de URL: loadGeoJson ya aborta la anterior y el slider ya hace debounce (500 ms).
  // Antes un debounce (leading) + un throttle (trailing) lanzaban dos peticiones idénticas por cada cambio.
  // clampedCqlFecha solo decide la media hora que se pide primero: moverla no debe relanzar la petición
  useEffect(() => {
    if (mapLeftLayer) loadGeoJson(geoJsonUrl, clampedCqlFecha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLeftLayer, geoJsonUrl, loadGeoJson, retry]);

  const mosaicFilters = useMemo(() => {
    if (!cqlFecha || !issueTime) return null;
    const datePart = cqlFecha.split(' ')[0];
    const { year, week } = getISOWeekAndYear(datePart);
    const forecastRelease = issueTime.includes(' ') ? issueTime.split(' ')[1].split(':')[0] : issueTime.split(':')[0]; // Extract "01" from "2024-07-30 01:00:00" or "01:00:00"

    // Ensure TIME is ISO with Z
    let timeIso = cqlFecha.trim().replace(' ', 'T');
    if (!timeIso.endsWith('Z')) timeIso += 'Z';

    return { year, week, forecastRelease, time: timeIso };
  }, [cqlFecha, issueTime]);

  const is025Layer = mapLeftLayer && mapLeftLayer.includes('025');
  const isTurbulenceLayer = mapLeftLayer && mapLeftLayer.includes('TURBULENCE');
  const isVisibilityLayer = mapLeftLayer && mapLeftLayer.includes('VISIBILITY');
  const isDustLayer = mapLeftLayer && mapLeftLayer.includes('DUST');
  const isIceLayer = mapLeftLayer && mapLeftLayer.includes('ICE');
  const isHighResLayer = selectedModel && selectedModel.includes('euro_1k_00625_15m');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Turbulence, visibility, dust e ICE van sin mapa de ground truth (ni su leyenda): la suya sale abierta al elegir la capa; Info la oculta
  useEffect(() => {
    setShowLegend(!!(isTurbulenceLayer || isVisibilityLayer || isDustLayer || isIceLayer));
  }, [isTurbulenceLayer, isVisibilityLayer, isDustLayer, isIceLayer, setShowLegend]);

  const pmFail = pmtilesFail?.url === pmtilesHistoricalUrl ? pmtilesFail : null;
  // Con el error del pmtiles (404 = no hay archivo) ya no hay teselas que esperar: antes se esperaba al idle del mapa, que
  // al cambiar de capa no llega hasta que acaba el fitBounds (800 ms)
  const tilesLoading = !!pmtilesHistoricalUrl && idleUrl !== pmtilesHistoricalUrl && !pmFail;
  // Tarjeta mientras el mapa no tiene nada de lo pedido (visibility sin su media hora, capa pmtiles sin teselas aún);
  // si ya se ve la media hora o la hora anterior, basta la píldora
  const blank = isVisibilityLayer ? slotTime !== clampedCqlFecha : !idleUrl;

  // Aviso cuando ya no se carga nada y la capa no se ha podido pintar: 404 del pmtiles o día de visibility sin puntos
  // = no hay datos; el resto (red, backend, GeoServer) = error. Polígonos CNN vacíos es normal (sin convección)
  const hasHeatmap = is025Layer || isTurbulenceLayer || isDustLayer || isIceLayer || (mapLeftLayer && mapLeftLayer.includes('01'));
  const notice = loading || tilesLoading ? null
    : (pmFail && !pmFail.notFound) || wfsFail === 'error' || (configFail && hasHeatmap && showMosaic && !pmtilesHistoricalUrl) ? 'error'
    : pmFail || (isVisibilityLayer && wfsFail === 'empty') ? 'empty'
    : null;
  const handleRetry = () => {
    setPmtilesFail(null);
    setIdleUrl(null);
    setRetry((n) => n + 1);
  };

  // Con el frame de la hora ya pintado, los demás del día (dust 8, el resto hasta 24, de 1 a 100 KB) se bajan en segundo
  // plano, como el radar de Windy: después cada paso del slider sale de la caché y no espera una petición al NAS por hora.
  // Una vez por capa + issue + día; si el día no tiene datos (pmFail) no se pide nada
  const prefetchedRef = useRef('');
  useEffect(() => {
    if (!pmtilesHistoricalUrl || idleUrl !== pmtilesHistoricalUrl || pmFail || !cqlFecha) return;
    const day = cqlFecha.slice(0, 10);
    const key = `${mapLeftLayer}|${issueTime}|${day}`;
    if (prefetchedRef.current === key) return;
    prefetchedRef.current = key;
    const hours = Array.from({ length: 24 }, (_, h) => buildPmtilesUrl(`${day} ${String(h).padStart(2, '0')}:00:00`));
    prefetchPmtiles([...new Set(hours.filter(Boolean))]);
  }, [pmtilesHistoricalUrl, idleUrl, pmFail, cqlFecha, issueTime, mapLeftLayer, buildPmtilesUrl]);

  const isConvectionLayer = hasHeatmap && !isTurbulenceLayer && !isDustLayer && !isIceLayer;
  const noticeBottom = !showLegend ? undefined : isConvectionLayer ? LEGEND_CLEARANCE : { xs: LEGEND_CLEARANCE, lg: 24 };

  // Sin datos para el día/issue elegido (pmtiles 404 o día de visibility sin puntos): como el aviso de run antiguo de la
  // página principal, se busca el día con datos más cercano (anterior o posterior) y el aviso dice cuál es y ofrece ir a
  // él. Si ese día es el elegido, lo que falta es el issue
  const nearestLayer = isDustLayer ? 'dust_historical' : isTurbulenceLayer ? 'turbulence_historical' : isIceLayer ? 'ice_historical'
    : isVisibilityLayer ? 'visibility' : is025Layer ? 'cnn_025_historical' : mapLeftLayer?.includes('01') ? 'cnn_01_historical' : null;
  const issueDay = issueTime?.slice(0, 10);
  const viewDay = cqlFecha?.slice(0, 10); // el día del calendario: de ahí se busca el día con datos más cercano
  const issueHourNow = issueTime ? extractIssueHour(issueTime) : null;
  const missingData = notice === 'empty' && !!nearestLayer;
  // Issues que ofrece el selector (dust no tiene el de las 07)
  const selectableHours = isDustLayer ? ['01', '13', '19'] : ['01', '07', '13', '19'];
  const { nearest, searching: searchingNearest } = useNearestData(nearestLayer, viewDay, selectableHours.join(','), missingData && !!onGoToIssue, retry);

  // El aviso sale en cuanto se sabe que no hay datos y con el mismo título mientras se busca el día más cercano y cuando
  // llega: solo cambia la segunda línea. Antes esperaba a la búsqueda (de 1 a 5 s en frío; visibility, 28 s) con el mapa
  // vacío y sin aviso; al mover el slider salía al momento porque la búsqueda ya estaba hecha para ese día
  const daysBack = (day, issue) => Math.round((Date.parse(day) - Date.parse(issue)) / 86400e3);
  const emptyText = `No data for issue D-${daysBack(viewDay, issueDay)} (${issueDay} ${issueHourNow}:00 UTC)`;

  // nearest = día más cercano con datos de su propio run (D-0, issueDate = date) y sus issues; de ese run, el issue de
  // ahora si lo tiene y si no el más próximo. La segunda línea dice solo lo que cambia respecto a la primera (la fecha,
  // el issue o los dos), sin repetir la fecha
  const suggestion = (() => {
    if (!missingData || !nearest?.date || !onGoToIssue) return null;
    const hour = nearest.hours.length ? closestHour(nearest.hours, issueHourNow) : null;
    if (!hour) return null;
    if (nearest.date === viewDay && nearest.issueDate === issueDay && hour === issueHourNow) return null; // falta solo esta hora del run
    const sameDate = nearest.date === viewDay;
    const sameIssue = daysBack(nearest.date, nearest.issueDate) === daysBack(viewDay, issueDay) && hour === issueHourNow;
    const issue = `issue D-${daysBack(nearest.date, nearest.issueDate)} ${hour}:00 UTC`;
    return {
      detail: `Closest available data: ${sameDate ? issue : sameIssue ? nearest.date : `${nearest.date}, ${issue}`}`,
      action: sameDate ? 'Go to issue' : 'Go to date',
      go: () => onGoToIssue(nearest.date, hour, nearest.issueDate),
    };
  })();

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Map
        id="mapLeft"
        onLoad={(e) => { mapRef.current = e.target; }} // <--- AGREGADO
        onIdle={() => setIdleUrl(pmtilesHistoricalUrl)}
        onError={(e) => {
          console.warn('[MapLeft]', e.sourceId || '', e.error?.message);
          // pmtiles lanza "Bad response code: 404" cuando el archivo de esa hora no existe en el NAS
          if (e.sourceId === 'pmtiles-source') setPmtilesFail({ url: pmtilesHistoricalUrl, notFound: /\b404\b/.test(e.error?.message) });
        }}
        initialViewState={{
          latitude: mapState.center[0],
          longitude: mapState.center[1],
          zoom: mapState.zoom
        }}
        mapStyle="/map-styles/kairos/forecast-metrics.json"
        attributionControl={false}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={['cnn-layer']}
        dragPan={isInteractive}
        scrollZoom={isInteractive}
        boxZoom={isInteractive}
        doubleClickZoom={isInteractive}
        touchZoomRotate={isInteractive}
        minZoom={MAP_CONFIG.minZoom}
        maxZoom={MAP_CONFIG.maxZoom}
        fadeDuration={0}
        collectResourceTiming={false}
      >
        <FirLayers visible={showFirs} />
        <MapLeftContent
          mapLeftLayer={mapLeftLayer}
          geoJsonData={geoJsonData}
          loading={loading}
          hasData={hasData}
          rectBounds={rectBounds}
          maskPolygon={maskPolygon}
          cqlFecha={cqlFecha}
          selectedTime={selectedTime}
          isInteractive={isInteractive}
          showMosaic={showMosaic}
          showGeoJson={showGeoJson}
          pmtilesHistoricalUrl={pmFail ? null : pmtilesHistoricalUrl}
          validTime={clampedCqlFecha}
          retry={retry}
        />
      </Map>

      <MapLoadingOverlay open={loading || tilesLoading} compact={!blank} layer={label} time={clampedCqlFecha} />
      {/* Con la leyenda abierta (abajo a la derecha) el aviso sube sobre ella. Convection comparte la fila con el mapa de
          ground truth y el mapa es estrecho: siempre; el resto tiene el mapa entero y solo en pantallas estrechas */}
      {suggestion
        ? <MapNotice kind="available" empty={emptyText} detail={suggestion.detail} onRetry={suggestion.go} action={suggestion.action} bottom={noticeBottom} />
        : <MapNotice kind={notice} empty={emptyText} detail={searchingNearest ? SEARCHING_NEAREST : undefined} onRetry={searchingNearest ? undefined : handleRetry} bottom={noticeBottom} />}

      <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.9 }}>
        <Badge
          badgeContent={
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
              RESET ZOOM
            </Typography>
          }
          color="error"
          overlap="circular"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            '& .MuiBadge-badge': {
              padding: '0 8px',
              height: '18px',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              top: '0px',
              left: '-2px',
              transform: 'translateX(-100%)',
              borderRadius: '10px',
            },
          }}
        >
          <IconButton
            onClick={handleReset}
            sx={{
              bgcolor: '#ffffff',
              color: '#38a477',
              '&:hover': { bgcolor: '#f5f5f5' },
              width: { xs: 28, md: 36 },
              height: { xs: 28, md: 36 },
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              borderRadius: 10,
              opacity: 0.9
            }}
            title="Reset view"
          >
            <HomeIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
          </IconButton>
        </Badge>

        {hasHeatmap && (
          <>
            <Badge
              badgeContent={
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                  HEATMAPS
                </Typography>
              }
              color="error"
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              sx={{
                '& .MuiBadge-badge': {
                  padding: '0 8px',
                  height: '18px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  top: '0px',
                  left: '-2px',
                  transform: 'translateX(-100%)',
                  borderRadius: '10px',
                },
              }}
            >
              <IconButton
                onClick={() => setShowMosaic(!showMosaic)}
                sx={{
                  bgcolor: showMosaic ? '#38a477' : '#ffffff',
                  color: showMosaic ? '#ffffff' : '#38a477',
                  '&:hover': { bgcolor: showMosaic ? '#38a477' : '#f5f5f5' },
                  width: { xs: 28, md: 36 },
                  height: { xs: 28, md: 36 },
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  borderRadius: 10,
                  opacity: 0.9
                }}
                title="Toggle Heatmap Layer"
              >
                <LayersIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
              </IconButton>
            </Badge>

            {!isTurbulenceLayer && !isDustLayer && !isIceLayer && (
            <Badge
              badgeContent={
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                  POLYGONS
                </Typography>
              }
              color="error"
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              sx={{
                '& .MuiBadge-badge': {
                  padding: '0 8px',
                  height: '18px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  top: '0px',
                  left: '-2px',
                  transform: 'translateX(-100%)',
                  borderRadius: '10px',
                },
              }}
            >
              <IconButton
                onClick={() => setShowGeoJson(!showGeoJson)}
                sx={{
                  bgcolor: showGeoJson ? '#38a477' : '#ffffff',
                  color: showGeoJson ? '#ffffff' : '#38a477',
                  '&:hover': { bgcolor: showGeoJson ? '#38a477' : '#f5f5f5' },
                  width: { xs: 28, md: 36 },
                  height: { xs: 28, md: 36 },
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  borderRadius: 10,
                  opacity: 0.9
                }}
                title="Toggle Polygons Layer"
              >
                <MapIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
              </IconButton>
            </Badge>
            )}
          </>
        )}

        <FirToggle visible={showFirs} onToggle={() => setShowFirs(!showFirs)} />

        {/* Sin ground truth (turbulence, visibility, dust) no hay métricas */}
        {metricsName && (
        <Box title='Metrics' sx={{ position: 'relative', display: 'block' }}>
          <Badge
            badgeContent={
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                AI METRICS
              </Typography>
            }
            color="error"
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              '& .MuiBadge-badge': {
                padding: '0 8px',
                height: '18px',
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                top: '0px',
                left: '-2px',
                transform: 'translateX(-100%)',
                borderRadius: '10px',
              },
            }}
          >
            <IconButton
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              sx={{
                bgcolor: isMenuOpen ? '#38a477' : '#ffffff',
                color: isMenuOpen ? '#ffffff' : '#38a477',
                '&:hover': { bgcolor: isMenuOpen ? '#38a477' : '#f5f5f5' },
                width: { xs: 28, md: 36 },
                height: { xs: 28, md: 36 },
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                borderRadius: 10,
                opacity: 0.9
              }}
            >
              <QueryStatsIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
            </IconButton>
          </Badge>

          {isMenuOpen && (
            <Box sx={{
              position: 'absolute',
              top: 28, // debajo de la etiqueta AI METRICS (18 px), sin taparla
              right: 40,
              width: '180px',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              padding: '15px',
              zIndex: 1100,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, borderBottom: '1px solid #444' }}>
                METRICS
              </Typography>
              {metricsName && metricsName.length > 0 ? (
                <MetricsInsideMap
                  onMenuOpen={() => setIsMenuOpen(true)}
                  onMenuClose={() => setIsMenuOpen(true)}
                  metricsNames={metricsName}
                  cqlFecha={clampedCqlFecha}
                  weekNumber={weekNumber}
                  issueTime={issueTime}
                  cqlSevere={cqlSevere}
                  handleRiskClick={handleRiskClick}
                />
              ) : (
                <Typography variant="caption" sx={{ color: '#aaa', fontStyle: 'italic' }}>
                  Loading metrics...
                </Typography>
              )}
            </Box>
          )}
        </Box>
        )}
      </Box>

      <Box title='Information' sx={{ position: 'absolute', bottom: 15, right: 15, zIndex: 10 }}>
        <IconButton
          onClick={handleToggleLegend}
          sx={{
            bgcolor: '#ffffff',
            color: '#38a477',
            '&:hover': { bgcolor: '#f5f5f5' },
            width: { xs: 28, md: 36 },
            height: { xs: 28, md: 36 },
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            borderRadius: 10,
            opacity: 0.9
          }}
        >
          <InfoIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
        </IconButton>
      </Box>

      {showLegend && (
        isTurbulenceLayer ? <LegendMap title="Turbulence" customItems={TURBULENCE_STOPS} />
        : isVisibilityLayer ? <LegendMap title="Visibility" customItems={Object.values(VISIBILITY_CLASSES)} />
        : isDustLayer ? <LegendMap title="Dust Severity" customItems={DUST_CLASSES} />
        : isIceLayer ? <LegendMap title="HAIC Risk" customItems={ICE_CLASSES} />
        : <LegendMap variant="left" />
      )}

      {/*
      {!hasData && !loading && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', zIndex: 2000, pointerEvents: 'none' }}>
          <p style={{ textAlign: 'center', lineHeight: 1.8, opacity: 0.95, fontSize: '0.95rem' }}>
            No storm or forecast data available for <strong>{cqlFecha.split(' ')[0]} {selectedTime} UTC</strong>.<br />
            <span style={{ display: 'block', marginTop: '0.4em', opacity: 0.8 }}>The selected time may be outside the forecast window or no data available.</span>
            <span style={{ display: 'inline-block', marginTop: '0.6em', fontStyle: 'italic', opacity: 0.8 }}>— try adjusting the timeline slider —</span>
          </p>
        </Box>
      )}
      */}
    </Box>
  );
};

export default MapLeft;